// Single source of truth for the 5 employee roles in the wizard.
// Each preset describes which sections are LOCKED on, which are CONFIGURABLE
// (with optional defaultOn), and which additional access flags / compensation
// fields apply. `getAuthorities()` walks the wizard state and returns the
// final flat ROLE_* array sent to the backend.

import {
  PERMISSION_SECTIONS,
  SECTION_BY_KEY,
  SECTION_KEYS,
} from './permissionSections';

export const ROLE_KEYS = {
  OWNER: 'owner',
  TECH_ADMIN: 'techAdmin',
  MANAGER: 'manager',
  EMPLOYEE: 'employee',
  SUPERVISOR: 'supervisor',
  READING_GUY: 'readingGuy',
  SECURITY: 'security',
};

// Additional access flags (rendered as toggles outside the section grid).
export const ADDITIONAL_ACCESS = {
  ACCOUNT_DASHBOARD: { key: 'accountDashboard', label: 'Account Dashboard' },
  LINK_ACCOUNTS: {
    key: 'linkAccounts',
    label: 'Link Accounts',
    role: 'ROLE_LINK_ACCOUNTS',
  },
  PREVIOUS_WEEKLY: {
    key: 'previousWeekly',
    label: 'Access to Previous Weekly Reports',
    role: 'ROLE_OPEN_ALL_WEEKLY_REPORT',
  },
  CUSTOMER_PHONE: {
    key: 'customerPhone',
    label: 'Customer Phone Number Access',
    role: 'ROLE_CUSTOMER_PRIVILEGIE',
  },
  CUSTOMER_MOBILE: {
    key: 'customerMobile',
    label: 'Customer Mobile Access',
    role: 'ROLE_CUSTOMER_MOBILE',
  },
  RAFFLE_MOBILE: {
    key: 'raffleMobile',
    label: 'Raffle Mobile Access',
    role: 'ROLE_RAFFLE_MOBILE',
  },
  OPEN_LAST_TWO_READINGS: {
    key: 'openLastTwoReadings',
    label: 'Open Last Two Readings',
    role: 'ROLE_OPEN_LAST_TWO_READINGS',
  },
  CUSTOMER_LOAN_REPORT: {
    key: 'customerLoanReport',
    label: 'Customer Loan Report',
    role: 'ROLE_CUSTOMER_LOAN_REPORT',
  },
  EMPLOYEE_SHORT_REPORT: {
    key: 'employeeShortReport',
    label: 'Employee Short Report',
    role: 'ROLE_EMPLOYEE_SHORT_REPORT',
  },
  ZELLE_REPORT: {
    key: 'zelleReport',
    label: 'Zelle Report',
    role: 'ROLE_ZELLE_REPORT',
  },
};

export const COMPENSATION = {
  INVESTOR: 'investor',
  MANAGER: 'manager',
  PER_HOUR: 'perHour',
  PAY_TYPE_TOGGLE: 'payTypeToggle',
};

const allSectionKeys = PERMISSION_SECTIONS.map((s) => s.key);

// ----- Role definitions -----------------------------------------------------

export const ROLE_PRESETS = {
  [ROLE_KEYS.OWNER]: {
    key: ROLE_KEYS.OWNER,
    label: 'Company Owner',
    icon: 'crown',
    color: '#D4A017',
    description: 'Full access — all features & settings',
    fixedSections: allSectionKeys, // every section is locked ON
    configurableSections: [],
    additionalAccess: [
      { ...ADDITIONAL_ACCESS.ACCOUNT_DASHBOARD, defaultOn: true, fixed: true },
      { ...ADDITIONAL_ACCESS.LINK_ACCOUNTS, defaultOn: true, fixed: true },
    ],
    compensation: COMPENSATION.INVESTOR,
    showEmployeeColor: false,
    showRfid: false,
    rfidOptional: false,
  },

  [ROLE_KEYS.TECH_ADMIN]: {
    key: ROLE_KEYS.TECH_ADMIN,
    label: 'Tech Admin',
    icon: 'shield',
    color: '#7C3AED',
    description: 'Remote admin — configurable permissions & locations',
    fixedSections: [],
    configurableSections: [
      { key: SECTION_KEYS.SECURITY, defaultOn: false },
      { key: SECTION_KEYS.DASHBOARD, defaultOn: false },
      { key: SECTION_KEYS.OPERATION_MONITOR, defaultOn: false },
      { key: SECTION_KEYS.READING, defaultOn: false },
      { key: SECTION_KEYS.ACCOUNTING, defaultOn: false },
      { key: SECTION_KEYS.SETTINGS, defaultOn: false },
    ],
    additionalAccess: [
      { ...ADDITIONAL_ACCESS.PREVIOUS_WEEKLY, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.CUSTOMER_PHONE, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.RAFFLE_MOBILE, defaultOn: false, fixed: false },
    ],
    // ROLE_TECH_ADMIN is the marker role that the CompanyPicker,
    // ManageUsersTable, and listAvailableCompanies all key off.
    // ROLE_ADMIN bypasses clock-in checks and is required for employee
    // CRUD on the backend (@Secured). ROLE_COMPANY grants access to the
    // company management pages (license, plan picker) when on NDS.
    // ROLE_MANAGE_USERS lets tech admins manage employees at their
    // linked locations. ROLE_SETTINGS is the parent that makes the
    // Settings sidebar item visible.
    extraRoles: [
      'ROLE_TECH_ADMIN',
      'ROLE_ADMIN',
      'ROLE_COMPANY',
      'ROLE_MANAGE_USERS',
      'ROLE_SETTINGS',
    ],
    compensation: COMPENSATION.PAY_TYPE_TOGGLE,
    showEmployeeColor: false,
    showRfid: false,
    rfidOptional: false,
    // Only show this card when the admin is on the NuVe System
    // company — tech admins are NuVe staff who manage client locations,
    // not employees of those client companies.
    nuveOnly: true,
  },

  [ROLE_KEYS.MANAGER]: {
    key: ROLE_KEYS.MANAGER,
    label: 'Manager',
    icon: 'building',
    color: '#5B6ABF',
    description: 'Fully customizable — pick the sections you want',
    // Only Dashboard is locked on (mirrors the Supervisor preset).
    // Every other section is a configurable toggle so admins can
    // tailor a Manager's access without being forced into the
    // default Security + Ops + Reading bundle.
    fixedSections: [SECTION_KEYS.DASHBOARD],
    configurableSections: [
      { key: SECTION_KEYS.SECURITY, defaultOn: true },
      { key: SECTION_KEYS.OPERATION_MONITOR, defaultOn: true },
      { key: SECTION_KEYS.READING, defaultOn: true },
      { key: SECTION_KEYS.ACCOUNTING, defaultOn: true },
      { key: SECTION_KEYS.SETTINGS, defaultOn: false },
    ],
    additionalAccess: [
      { ...ADDITIONAL_ACCESS.CUSTOMER_PHONE, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.CUSTOMER_MOBILE, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.RAFFLE_MOBILE, defaultOn: false, fixed: false },
      {
        ...ADDITIONAL_ACCESS.OPEN_LAST_TWO_READINGS,
        defaultOn: false,
        fixed: false,
      },
    ],
    // Managers are granted ROLE_ADMIN so they bypass clock-in, are
    // recognized by isManager() in the Report view, and are treated
    // as admin-tier in backend cascades. ROLE_MANAGER is the
    // employee-type marker used by features that need to distinguish
    // Manager from Reading Guy (both have ROLE_ADMIN).
    extraRoles: ['ROLE_ADMIN', 'ROLE_MANAGER'],
    compensation: COMPENSATION.MANAGER,
    showEmployeeColor: false,
    showRfid: false,
    rfidOptional: false,
  },

  [ROLE_KEYS.EMPLOYEE]: {
    key: ROLE_KEYS.EMPLOYEE,
    label: 'Employee',
    icon: 'person',
    color: '#6B7280',
    description: 'Dashboard access only',
    fixedSections: [SECTION_KEYS.DASHBOARD],
    configurableSections: [],
    additionalAccess: [
      { ...ADDITIONAL_ACCESS.CUSTOMER_PHONE, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.CUSTOMER_MOBILE, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.RAFFLE_MOBILE, defaultOn: false, fixed: false },
      {
        ...ADDITIONAL_ACCESS.CUSTOMER_LOAN_REPORT,
        defaultOn: false,
        fixed: false,
      },
      {
        ...ADDITIONAL_ACCESS.EMPLOYEE_SHORT_REPORT,
        defaultOn: false,
        fixed: false,
      },
      { ...ADDITIONAL_ACCESS.ZELLE_REPORT, defaultOn: false, fixed: false },
    ],
    compensation: COMPENSATION.PER_HOUR,
    showEmployeeColor: true,
    showRfid: true,
    rfidOptional: false,
    note: 'Employee role has Dashboard access only — no other sections can be enabled.',
  },

  [ROLE_KEYS.SUPERVISOR]: {
    key: ROLE_KEYS.SUPERVISOR,
    label: 'Supervisor',
    icon: 'shield',
    color: '#2563EB',
    description: 'Dashboard + configurable access',
    fixedSections: [SECTION_KEYS.DASHBOARD],
    configurableSections: [
      { key: SECTION_KEYS.SECURITY, defaultOn: false },
      { key: SECTION_KEYS.OPERATION_MONITOR, defaultOn: false },
      { key: SECTION_KEYS.READING, defaultOn: false },
      { key: SECTION_KEYS.ACCOUNTING, defaultOn: false },
    ],
    additionalAccess: [
      { ...ADDITIONAL_ACCESS.CUSTOMER_PHONE, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.CUSTOMER_MOBILE, defaultOn: false, fixed: false },
      { ...ADDITIONAL_ACCESS.RAFFLE_MOBILE, defaultOn: false, fixed: false },
    ],
    // Employee-type marker. Used by feature gates that need to
    // distinguish supervisors from other dashboard-only users.
    extraRoles: ['ROLE_SUPERVISOR'],
    compensation: COMPENSATION.PAY_TYPE_TOGGLE,
    showEmployeeColor: true,
    showRfid: true,
    rfidOptional: true,
  },

  [ROLE_KEYS.READING_GUY]: {
    key: ROLE_KEYS.READING_GUY,
    label: 'Reading Guy',
    icon: 'book',
    color: '#3B5998',
    description: 'Operations, Reading & optional Accounting',
    // Note: ROLE_DASHBOARD (the sidebar Dashboard route) is auto-granted
    // to every non-owner role via getAuthorities' base set, so Reading
    // Guy gets the Dashboard menu item without needing the full Dashboard
    // section (Transaction, Ticket Out, Bonus, etc.) locked on.
    fixedSections: [],
    configurableSections: [
      {
        key: SECTION_KEYS.OPERATION_MONITOR,
        defaultOn: true,
        badge: 'DEFAULT ON',
      },
      { key: SECTION_KEYS.READING, defaultOn: false },
      { key: SECTION_KEYS.ACCOUNTING, defaultOn: false },
      { key: SECTION_KEYS.SETTINGS, defaultOn: false },
    ],
    // Reading Guy sees the Settings section narrowed to just the
    // Setup Machines toggle — other Settings subs (rules, manage
    // bonus, manage employees) are hidden for this role.
    fixedSubs: {
      [SECTION_KEYS.SETTINGS]: ['setupMachines'],
    },
    additionalAccess: [
      {
        ...ADDITIONAL_ACCESS.OPEN_LAST_TWO_READINGS,
        defaultOn: false,
        fixed: false,
      },
    ],
    // ROLE_ADMIN for admin-tier treatment (same as Manager);
    // ROLE_READING_GUY is the employee-type marker that distinguishes
    // Reading Guy from Manager for feature gates like the weekly-report
    // prior-week edit lockout.
    extraRoles: ['ROLE_ADMIN', 'ROLE_READING_GUY'],
    compensation: COMPENSATION.PAY_TYPE_TOGGLE,
    showEmployeeColor: false,
    showRfid: false,
    rfidOptional: false,
  },

  [ROLE_KEYS.SECURITY]: {
    key: ROLE_KEYS.SECURITY,
    label: 'Security',
    icon: 'shield',
    color: '#10B981',
    description: 'Security Dashboard only — gate check-in screen',
    fixedSections: [SECTION_KEYS.SECURITY],
    // fixedSubs locks the Security section to ONLY the security
    // dashboard sub. Other Security subs (e.g. Customer Wallet Pass)
    // are intentionally hidden and never granted to this role.
    fixedSubs: {
      [SECTION_KEYS.SECURITY]: ['securityDashboard'],
    },
    configurableSections: [],
    additionalAccess: [],
    compensation: COMPENSATION.PER_HOUR,
    showEmployeeColor: true,
    showRfid: true,
    rfidOptional: false,
    // Hide this role card unless the company's plan actually grants
    // ROLE_SECURITY (Alpha plan, or Pro Elite + Security Dashboard
    // add-on). Otherwise the card would appear with zero permissions.
    requiredRoles: ['ROLE_SECURITY'],
    note: 'Security role has access to the Security Dashboard only — no other features can be enabled.',
  },
};

export const ROLE_LIST = [
  ROLE_PRESETS[ROLE_KEYS.OWNER],
  ROLE_PRESETS[ROLE_KEYS.TECH_ADMIN],
  ROLE_PRESETS[ROLE_KEYS.MANAGER],
  ROLE_PRESETS[ROLE_KEYS.EMPLOYEE],
  ROLE_PRESETS[ROLE_KEYS.SUPERVISOR],
  ROLE_PRESETS[ROLE_KEYS.READING_GUY],
  ROLE_PRESETS[ROLE_KEYS.SECURITY],
];

// Returns the union of fixed + configurable section keys for a role.
export function getVisibleSectionKeys(roleKey) {
  const preset = ROLE_PRESETS[roleKey];
  if (!preset) return [];
  const set = new Set(preset.fixedSections);
  preset.configurableSections.forEach((cs) => set.add(cs.key));
  return Array.from(set);
}

export function isSectionFixed(roleKey, sectionKey) {
  const preset = ROLE_PRESETS[roleKey];
  return !!preset && preset.fixedSections.includes(sectionKey);
}

export function getSectionConfig(roleKey, sectionKey) {
  const preset = ROLE_PRESETS[roleKey];
  if (!preset) return null;
  return (
    preset.configurableSections.find((cs) => cs.key === sectionKey) || null
  );
}

// Build the initial wizard toggle state when a role is picked.
export function buildInitialState(roleKey) {
  const preset = ROLE_PRESETS[roleKey];
  const sectionToggles = {};
  const subToggles = {};

  if (!preset) {
    return { sectionToggles, subToggles, additionalToggles: {} };
  }

  // Fixed sections: ON. All subs ON unless the preset narrows the
  // set via fixedSubs[sectionKey], in which case only those subs are
  // enabled (the rest are explicitly off and hidden in the wizard).
  preset.fixedSections.forEach((sectionKey) => {
    sectionToggles[sectionKey] = true;
    const allowedKeys = preset.fixedSubs && preset.fixedSubs[sectionKey];
    SECTION_BY_KEY[sectionKey].subs.forEach((sub) => {
      subToggles[sub.key] = allowedKeys ? allowedKeys.includes(sub.key) : true;
    });
  });

  // Configurable sections: defaultOn determines initial section + sub state
  preset.configurableSections.forEach((cs) => {
    sectionToggles[cs.key] = !!cs.defaultOn;
    const allowedKeys = preset.fixedSubs && preset.fixedSubs[cs.key];
    SECTION_BY_KEY[cs.key].subs.forEach((sub) => {
      if (allowedKeys && !allowedKeys.includes(sub.key)) {
        subToggles[sub.key] = false;
        return;
      }
      subToggles[sub.key] = !!cs.defaultOn;
    });
  });

  const additionalToggles = {};
  preset.additionalAccess.forEach((acc) => {
    additionalToggles[acc.key] = !!acc.defaultOn;
  });

  return { sectionToggles, subToggles, additionalToggles };
}

// Walk the wizard state and produce the flat ROLE_* array sent to backend.
export function getAuthorities({
  roleKey,
  sectionToggles,
  subToggles,
  additionalToggles,
  allRoles = [],
}) {
  const preset = ROLE_PRESETS[roleKey];
  const out = new Set(['ROLE_DASHBOARD']);

  if (!preset) return Array.from(out);

  // Helper: emit a sub's primary role plus any extraRoles bundled
  // with it (e.g. Setup Machines grants ROLE_MACHINE + ROLE_GAME +
  // ROLE_ASSIGN_MACHINE from a single toggle).
  const emitSubRoles = (sub) => {
    if (sub.role) out.add(sub.role);
    if (Array.isArray(sub.extraRoles)) {
      sub.extraRoles.forEach((r) => out.add(r));
    }
  };

  // Owner = everything
  if (roleKey === ROLE_KEYS.OWNER) {
    allRoles.forEach((r) => out.add(r));
    out.add('ROLE_MASTER_ADMIN');
    out.add('ROLE_ADMIN');
    // Owner-only flags
    PERMISSION_SECTIONS.forEach((section) => {
      section.subs.forEach((sub) => emitSubRoles(sub));
      if (section.parentRole) out.add(section.parentRole);
    });
    return Array.from(out);
  }

  const visibleSections = getVisibleSectionKeys(roleKey);
  visibleSections.forEach((sectionKey) => {
    const section = SECTION_BY_KEY[sectionKey];
    const sectionEnabled = !!sectionToggles[sectionKey];
    if (!sectionEnabled) return;

    const allowedSubKeys = preset.fixedSubs && preset.fixedSubs[sectionKey];
    let anySubOn = false;
    section.subs.forEach((sub) => {
      if (allowedSubKeys && !allowedSubKeys.includes(sub.key)) return;
      if (subToggles[sub.key]) {
        emitSubRoles(sub);
        anySubOn = true;
      }
    });
    if (anySubOn && section.parentRole) {
      out.add(section.parentRole);
    }
  });

  // Additional access flags (those with a `role` are turned into ROLE_*)
  preset.additionalAccess.forEach((acc) => {
    if (acc.role && additionalToggles[acc.key]) {
      out.add(acc.role);
      // Customer Phone Access requires ROLE_CUSTOMER as parent
      if (acc.role === 'ROLE_CUSTOMER_PRIVILEGIE') {
        out.add('ROLE_CUSTOMER');
      }
      // Customer Mobile Access requires ROLE_CUSTOMER as parent
      if (acc.role === 'ROLE_CUSTOMER_MOBILE') {
        out.add('ROLE_CUSTOMER');
      }
      // Raffle Mobile Access requires ROLE_RAFFLE as parent
      if (acc.role === 'ROLE_RAFFLE_MOBILE') {
        out.add('ROLE_RAFFLE');
      }
      // Open all weekly requires ROLE_ACCOUNTING parent
      if (acc.role === 'ROLE_OPEN_ALL_WEEKLY_REPORT') {
        out.add('ROLE_ACCOUNTING');
      }
      // Standalone accounting reports also require ROLE_ACCOUNTING parent
      if (
        acc.role === 'ROLE_CUSTOMER_LOAN_REPORT' ||
        acc.role === 'ROLE_EMPLOYEE_SHORT_REPORT' ||
        acc.role === 'ROLE_ZELLE_REPORT'
      ) {
        out.add('ROLE_ACCOUNTING');
      }
    }
  });

  // Preset-level extra roles that aren't tied to a section/sub or
  // additional-access toggle (e.g. Manager → ROLE_ADMIN).
  if (Array.isArray(preset.extraRoles)) {
    preset.extraRoles.forEach((r) => out.add(r));
  }

  return Array.from(out);
}

// Best-effort role detection from an existing user's authority array.
// Used when opening the wizard in EDIT mode.
export function inferRoleKey(authorities = []) {
  const set = new Set(authorities);

  // Tech Admin: has ROLE_TECH_ADMIN but is NOT the company owner
  // (no ROLE_OWNER / ROLE_MASTER_ADMIN). Distinguishing this from
  // Owner is critical so the wizard opens the right card with the
  // configurable-accounting toggle.
  if (
    set.has('ROLE_TECH_ADMIN') &&
    !set.has('ROLE_MASTER_ADMIN') &&
    !set.has('ROLE_OWNER')
  ) {
    return ROLE_KEYS.TECH_ADMIN;
  }

  if (
    set.has('ROLE_MASTER_ADMIN') ||
    set.has('ROLE_OWNER') ||
    set.has('ROLE_NUVE_MASTER_ADMIN')
  ) {
    return ROLE_KEYS.OWNER;
  }

  // Fast-path: explicit employee-type markers beat heuristics.
  if (set.has('ROLE_MANAGER')) return ROLE_KEYS.MANAGER;
  if (set.has('ROLE_READING_GUY')) return ROLE_KEYS.READING_GUY;
  if (set.has('ROLE_SUPERVISOR')) return ROLE_KEYS.SUPERVISOR;

  const sectionHasAnySub = (sectionKey) =>
    SECTION_BY_KEY[sectionKey].subs.some((s) => set.has(s.role));

  const hasSecurity = sectionHasAnySub(SECTION_KEYS.SECURITY);
  const hasDashboard = sectionHasAnySub(SECTION_KEYS.DASHBOARD);
  const hasOps = sectionHasAnySub(SECTION_KEYS.OPERATION_MONITOR);
  const hasReading = sectionHasAnySub(SECTION_KEYS.READING);
  // Accounting section subs EXCLUDING the three standalone report toggles
  // that are also exposed as additional-access flags on the Employee role —
  // otherwise an Employee with just "Customer Loan Report" enabled would
  // be misclassified as Supervisor.
  const EMPLOYEE_STANDALONE_REPORT_ROLES = new Set([
    'ROLE_CUSTOMER_LOAN_REPORT',
    'ROLE_EMPLOYEE_SHORT_REPORT',
    'ROLE_ZELLE_REPORT',
  ]);
  const hasAccounting = SECTION_BY_KEY[SECTION_KEYS.ACCOUNTING].subs.some(
    (s) => set.has(s.role) && !EMPLOYEE_STANDALONE_REPORT_ROLES.has(s.role)
  );
  const hasSettings = sectionHasAnySub(SECTION_KEYS.SETTINGS);

  // Security: only Security Dashboard, nothing else.
  if (
    set.has('ROLE_SECURITY') &&
    !hasDashboard &&
    !hasOps &&
    !hasReading &&
    !hasAccounting &&
    !hasSettings
  ) {
    return ROLE_KEYS.SECURITY;
  }

  // Manager: ROLE_ADMIN + security + dashboard + ops + reading (may
  // optionally have accounting and/or settings enabled).
  if (
    set.has('ROLE_ADMIN') &&
    hasSecurity &&
    hasDashboard &&
    hasOps &&
    hasReading
  ) {
    return ROLE_KEYS.MANAGER;
  }

  // Reading guy: no dashboard section subs, no security, has ops/reading/accounting.
  // (ROLE_DASHBOARD route is auto-granted to every non-owner role, so its
  // presence doesn't disqualify — we look at the dashboard SECTION subs.)
  if (
    !hasDashboard &&
    !hasSecurity &&
    (hasOps || hasReading || hasAccounting)
  ) {
    return ROLE_KEYS.READING_GUY;
  }

  // Plain employee: only dashboard
  if (
    hasDashboard &&
    !hasSecurity &&
    !hasOps &&
    !hasReading &&
    !hasAccounting &&
    !hasSettings
  ) {
    return ROLE_KEYS.EMPLOYEE;
  }

  // Anything else with dashboard plus extras → supervisor
  return ROLE_KEYS.SUPERVISOR;
}

// Rebuild wizard toggle state from an existing user's authority array,
// after the role has been inferred.
export function buildStateFromAuthorities(roleKey, authorities = []) {
  const preset = ROLE_PRESETS[roleKey];
  const base = buildInitialState(roleKey);
  if (!preset) return base;

  const set = new Set(authorities);

  // For configurable sections, derive section/sub toggles from existing authorities.
  preset.configurableSections.forEach((cs) => {
    const section = SECTION_BY_KEY[cs.key];
    const allowedKeys = preset.fixedSubs && preset.fixedSubs[cs.key];
    let anyOn = false;
    section.subs.forEach((sub) => {
      if (allowedKeys && !allowedKeys.includes(sub.key)) {
        base.subToggles[sub.key] = false;
        return;
      }
      // A sub is considered "on" if its primary role is granted OR
      // any of its bundled extraRoles is granted (e.g. an existing
      // user with ROLE_GAME but not ROLE_MACHINE still flips the
      // consolidated Setup Machines toggle on).
      const on =
        set.has(sub.role) ||
        (Array.isArray(sub.extraRoles) &&
          sub.extraRoles.some((r) => set.has(r)));
      base.subToggles[sub.key] = on;
      if (on) anyOn = true;
    });
    base.sectionToggles[cs.key] = anyOn;
  });

  // Additional access toggles
  preset.additionalAccess.forEach((acc) => {
    if (acc.role) {
      base.additionalToggles[acc.key] = set.has(acc.role);
    }
  });

  return base;
}

export function getEnabledSubCount(sectionKey, subToggles) {
  const section = SECTION_BY_KEY[sectionKey];
  if (!section) return { enabled: 0, total: 0 };
  let enabled = 0;
  section.subs.forEach((sub) => {
    if (subToggles[sub.key]) enabled += 1;
  });
  return { enabled, total: section.subs.length };
}
